import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { formatISO } from 'date-fns';
import { environment } from 'environment/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OccupancyFilter } from 'shared/enums/occupancy-filter.enum';
import { ApiParams, BaseApiService } from 'shared/services/base-api.service';
import { CONTROLLERS } from 'shared/services/controllers';
import {
  ApiResponse,
  IShipmentInfo,
  ITrailer,
  ITrailerFiltersForm,
  ITrailerFiltersRequest,
  ITrailerForm,
  ITrailerHistory,
  SiteLocation,
} from 'shared/types';
import { OptionsToParams } from 'shared/utility';

export interface IGetTrailersBySiteOptions {
  trailerNumber?: string;
  scacCode?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class TrailerApiService extends BaseApiService<
  ApiResponse<{ data: ITrailer[] }>,
  ApiResponse<ITrailer>,
  ApiResponse<{ id: string }>
> {
  constructor(public override http: HttpClient) {
    super(http, environment.yardOpsApiBaseUrl);
  }

  public controllerName(): string {
    return CONTROLLERS.trailers;
  }

  public arrive(trailer: ITrailerForm): Observable<ApiResponse<{ id: string; smsProcessedSuccessfully: boolean }>> {
    return this.http.post<
      ApiResponse<{
        id: string;
        smsProcessedSuccessfully: boolean;
      }>
    >(`${this.api}/${this.controllerName()}/arrivals`, trailer);
  }

  public depart(trailer: Partial<ITrailer>): Observable<ApiResponse<{ id: string }>> {
    return this.http.put<ApiResponse<{ id: string }>>(
      `${this.api}/${this.controllerName()}/${trailer.id}/departures`,
      trailer,
    );
  }

  public reArrive(trailer: Partial<ITrailer>): Observable<ApiResponse<{ id: string }>> {
    return this.http.put<ApiResponse<{ id: string }>>(
      `${this.api}/${this.controllerName()}/arrivals/${trailer.id}`,
      trailer,
    );
  }

  public importCsv(siteId: string, file: File): Observable<ApiResponse<{ imported: boolean; importedAt: Date }>> {
    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('siteId', siteId);
    return this.http.post<
      ApiResponse<{
        imported: boolean;
        importedAt: Date;
      }>
    >(`${this.api}/${this.controllerName()}/import/csv`, formData);
  }

  public getTrailers(
    filters?: ITrailerFiltersRequest,
    pagination?: { pageNumber: number; pageSize: number },
  ): Observable<ApiResponse<{ items: ITrailer[] }>> {
    let params = new HttpParams();
    if (filters) {
      params = this.processFilters(filters, params);
    }
    if (pagination) {
      params = params.set('pageNumber', pagination.pageNumber.toString());
      params = params.set('pageSize', pagination.pageSize.toString());
    }

    return this.http.get<ApiResponse<{ items: ITrailer[] }>>(`${this.api}/${this.controllerName()}`, { params });
  }

  public getTrailerNumbers(options: ApiParams<IGetTrailersBySiteOptions>): Observable<
    ApiResponse<{
      items: {
        trailerNumber: string;
        disabled: boolean;
      }[];
    }>
  > {
    const params = OptionsToParams.processOptionsToParams(new HttpParams(), options);

    return this.http.get<
      ApiResponse<{
        items: {
          trailerNumber: string;
          disabled: boolean;
        }[];
      }>
    >(`${this.api}/${CONTROLLERS.trailers}/trailer-numbers`, { params });
  }

  public getAvailableLocations(
    trailerId: string | null = null,
    occupancyFilter: OccupancyFilter = OccupancyFilter.Available,
  ): Observable<ApiResponse<SiteLocation[]>> {
    let params = new HttpParams();
    if (trailerId) {
      params = params.set('trailerId', trailerId);
    }
    if (occupancyFilter) {
      params = params.set('occupancyFilter', occupancyFilter);
    }
    return this.http.get<ApiResponse<SiteLocation[]>>(`${this.api}/${CONTROLLERS.locations}`, { params });
  }

  public getScacs(trailerNumber?: string): Observable<
    ApiResponse<{
      scacs: {
        code: string;
        name: string;
        disabled: boolean;
      }[];
    }>
  > {
    let params = new HttpParams();
    if (trailerNumber) {
      params = params.append('trailerNumber', trailerNumber);
    }

    return this.http.get<
      ApiResponse<{
        scacs: {
          code: string;
          name: string;
          disabled: boolean;
        }[];
      }>
    >(`${this.api}/api/scacs`, { params });
  }

  public getShipments(): Observable<ApiResponse<IShipmentInfo[]>> {
    return this.http.get<ApiResponse<IShipmentInfo[]>>(`${this.api}/${CONTROLLERS.trailers}/shipment-numbers`);
  }

  public getTrailersCSV(
    filters?: ITrailerFiltersForm,
  ): Observable<{ progress: number; blob?: Blob; filename?: string }> {
    let params = new HttpParams();

    if (filters) {
      params = this.processFilters(filters, params);
    }

    return this.http
      .get(`${this.api}/${this.controllerName()}/export`, {
        params,
        responseType: 'blob' as 'json',
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event: HttpEvent<unknown>) => {
          switch (event.type) {
            case HttpEventType.DownloadProgress: {
              const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
              return { progress };
            }

            case HttpEventType.Response: {
              const blob = event.body as Blob;
              const filename = this.extractFilenameFromContentDisposition(event.headers.get('content-disposition'));
              return { progress: event.status == 204 ? -1 : 100, blob, filename };
            }

            default:
              return { progress: 0 };
          }
        }),
      );
  }

  public getHistory(
    trailerId: string,
    dateFrom: Date | null,
    dateTo?: Date | null,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<ApiResponse<{ items: ITrailerHistory[] }>> {
    const params = this.getHistoryParams(dateFrom, dateTo, pageNumber, pageSize);
    return this.http.get<ApiResponse<{ items: ITrailerHistory[] }>>(
      `${this.api}/${this.controllerName()}/${trailerId}/events`,
      {
        params,
      },
    );
  }

  public downloadHistoryExport(
    trailerId: string,
    dateFrom: Date | null,
    dateTo?: Date | null,
  ): Observable<{ trailerHistoryData: Blob; fileName: string } | undefined> {
    let params = new HttpParams();
    if (dateFrom && dateTo) {
      params = params.set('startDate', dateFrom.toISOString());
      params = params.set('endDate', dateTo.toISOString());
    }

    return this.http
      .get(`${this.api}/${this.controllerName()}/${trailerId}/events/export`, {
        params,
        responseType: 'blob',
        observe: 'events',
      })
      .pipe(
        map((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.Response) {
            const contentDisposition = event.headers.get('content-disposition');
            let fileName = 'history.csv'; // Default filename

            if (contentDisposition) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
              if (matches != null && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '').trim(); // Remove quotes
              } else {
                const utf8Match = /filename\*=UTF-8''([^;]+)/.exec(contentDisposition);
                if (utf8Match != null && utf8Match[1]) {
                  fileName = decodeURIComponent(utf8Match[1].trim());
                }
              }
            }

            return { trailerHistoryData: event.body as Blob, fileName };
          }
          return undefined;
        }),
      );
  }

  /* Private methods */

  private processFilters(filters: ITrailerFiltersRequest, params: HttpParams): HttpParams {
    if (!filters) return params;

    Object.entries(filters).forEach(([key, value]) => {
      let values = [];
      if (value === undefined || value === null) return;
      if (typeof value === 'boolean') {
        params = params.append(key, value.toString());
      } else {
        if (Array.isArray(value)) {
          values = value;
        } else if (value && typeof value === 'object') {
          values = Object.values(value);
        } else {
          values = [value];
        }

        values
          .filter((x) => x !== null && x !== undefined && x !== '')
          .forEach((value) => {
            params = params.append(key, value as string);
          });
      }
    });
    return params;
  }

  private getHistoryParams(dateFrom: Date | null, dateTo?: Date | null, pageNumber = 1, pageSize = 100): HttpParams {
    let params = new HttpParams();
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());
    if (dateFrom && dateTo) {
      params = params.set('dateFrom', formatISO(dateFrom));
      params = params.set('dateTo', formatISO(dateTo));
    }
    return params;
  }

  private extractFilenameFromContentDisposition(contentDisposition: string | null): string {
    if (!contentDisposition) return 'trailers.csv';

    // 1. Try filename* (RFC 5987 encoding)
    const filenameStarMatch = /filename\*\s*=\s*UTF-8''([^;\n]*)/i.exec(contentDisposition);
    if (filenameStarMatch?.[1]) {
      try {
        return decodeURIComponent(filenameStarMatch[1].trim());
      } catch {
        // Fall through to filename
      }
    }

    // 2. Fallback to regular filename
    const filenameMatch = /filename\s*=\s*"?([^"]+)"?/.exec(contentDisposition);
    if (filenameMatch?.[1]) {
      return filenameMatch[1].trim();
    }

    return 'trailers.csv';
  }
}
